import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { deleteIcon } from "assets/images";
import CustomPagination from "components/customPagination";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import NoRecords from "components/NoRecords";
import moment from "moment";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  AddPersonalAndSmartGoalsPayload,
  DeletePersonalAndSmartGoalsPayload,
  DeletePersonalAndSmartGoalsSuccessPayload,
} from "store/slpForm/types";
import {
  GetAllSLPGoalsAndAssessmentPayload,
  GetAllSLPGoalsAndAssessmentPayloadValues,
  GetAllSLPGoalsAndAssessmentSuccessPayload,
} from "store/studentProfileDetails/types";
import {
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_TEN_PAGE_SIZE,
  NO_DATA,
} from "utils/constants/constant";
import {
  DELETE_PERSONAL_SMART_GOALS_CONFIRMATION_MSG,
  PERSONAL_SMART_GOALS_DELETED_SUCCESS_MSG,
} from "utils/constants/Messages";
import { ratingDisplayMap, TableCellAlign } from "utils/constants/slp";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  IGroupedGoalsList,
  IGroupedGoalsListMeta,
} from "utils/interfaces/student-profile-details";
import { SLPParticipation } from "utils/enums/slp";
import AddSLPGoalsModal from "../AddSLPGoalsModal";

const columns = [
  { key: "student", label: "Student", width: 120 },
  { key: "studentGoals", label: "Student Goal", width: 250, minWidth: 200 },
  { key: "smartGoals", label: "Smart Goal", width: 250, minWidth: 200 },
  { key: "importance", label: "Importance", width: 100, align: "center" },
  { key: "difficulty", label: "Difficulty", width: 100, align: "center" },
  { key: "goalWeight", label: "Goal Weight", width: 100, align: "center" },
  { key: "baselineLevel", label: "Baseline Level", width: 250, minWidth: 200 },
  { key: "achievementRatings", label: "Achievement Rating", width: 200 },
  { key: "variance", label: "Variance", width: 250, minWidth: 200 },
  { key: "goalAssessedDate", label: "Date Assessed", width: 120 },
  { key: "actions", label: "Action", width: 120 },
];

interface ISLPGoalsAndAssessmentProps {
  getAllSLPGoalsAndAssessmentRequest: (
    payload: GetAllSLPGoalsAndAssessmentPayload
  ) => void;
  studentId: number;
  nextOrCurrentSLPId: number;
  studentName: string | undefined;
  deletePersonalAndSmartGoalsRequest: (
    payload: DeletePersonalAndSmartGoalsPayload
  ) => {};
  addPersonalAndSmartGoalsRequest: (
    payload: AddPersonalAndSmartGoalsPayload
  ) => {};
  slpParticipation: SLPParticipation;
}

const SLPGoalsAndAssessment: React.FC<ISLPGoalsAndAssessmentProps> = (
  props
) => {
  const {
    getAllSLPGoalsAndAssessmentRequest,
    studentId,
    nextOrCurrentSLPId,
    studentName,
    deletePersonalAndSmartGoalsRequest,
    addPersonalAndSmartGoalsRequest,
    slpParticipation,
  } = props;

  const [slpGroupedGoalsList, setSlpGroupedGoalsList] = useState<
    IGroupedGoalsList[]
  >([]);
  const [slpGroupedGoalsListMeta, setslpGroupedGoalsListMeta] =
    useState<IGroupedGoalsListMeta>();
  const [page, setPage] = useState<string>(DEFAULT_TEN_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [isOpenDocumentDeleteModal, setIsOpenDocumentDeleteModal] =
    useState<boolean>(false);
  const [deleteGoalId, setDeleteGoalId] = useState<number | null>(null);
  const [openGoalsModal, setOpenGoalsModal] = useState<boolean>(false);

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(DEFAULT_PAGE_NO);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const getAllSLPGoalsAndAssessment = () => {
    if (getAllSLPGoalsAndAssessmentRequest) {
      showLoaderForBatchApiCall();
      const values: GetAllSLPGoalsAndAssessmentPayloadValues = {
        studentId,
        pageNo,
        pageSize: Number(page),
      };

      const payload = {
        values,
        callback: (response: GetAllSLPGoalsAndAssessmentSuccessPayload) => {
          hideLoaderForBatchApiCall();
          setSlpGroupedGoalsList(response.groupedList);
          setslpGroupedGoalsListMeta(response.groupedListPagination);
        },
      };
      getAllSLPGoalsAndAssessmentRequest(payload);
    }
  };

  const handleDocumentDeleteCloseModal = () => {
    setIsOpenDocumentDeleteModal(false);
    setDeleteGoalId(null);
  };

  const handleDeleteModal = (goalId: number) => {
    setDeleteGoalId(goalId);
    setIsOpenDocumentDeleteModal(true);
  };

  const handleDeleteGoal = useCallback(
    (id: number) => {
      if (deletePersonalAndSmartGoalsRequest) {
        showLoaderForBatchApiCall();
        const payload: DeletePersonalAndSmartGoalsPayload = {
          values: { id },
          callback: (response: DeletePersonalAndSmartGoalsSuccessPayload) => {
            hideLoaderForBatchApiCall();
            if (response.success) {
              toast.success(PERSONAL_SMART_GOALS_DELETED_SUCCESS_MSG);
              getAllSLPGoalsAndAssessment();
            }
            handleDocumentDeleteCloseModal();
          },
        };
        deletePersonalAndSmartGoalsRequest(payload);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deletePersonalAndSmartGoalsRequest]
  );

  const handleDeleteConfirm = () => {
    if (deleteGoalId) handleDeleteGoal(deleteGoalId);
  };

  useEffect(() => {
    getAllSLPGoalsAndAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo]);

  const handleAddSLPGoals = () => {
    setOpenGoalsModal(true);
  };

  const updateGoalsListCallback = () => {
    getAllSLPGoalsAndAssessment();
  };

  return (
    <Grid item xs={12}>
      <Card>
        <Box className="card-inner-head mb-24">
          <Typography variant="h3" sx={{ fontWeight: 500 }}>
            SLP Goals
          </Typography>
          <Box>
            <Button
              variant="contained"
              onClick={handleAddSLPGoals}
              disabled={
                slpParticipation === SLPParticipation.NoFurtherSLPRequired &&
                !nextOrCurrentSLPId
              }
            >
              Add New Goals
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table className="table-h-md">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={(col.align ?? "left") as TableCellAlign}
                    sx={{
                      maxWidth: col.width,
                      minWidth: col.minWidth || col.width,
                      width: col.width,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {slpGroupedGoalsList.map((group) => (
                <Fragment key={`group-${group.slpNumber}`}>
                  <TableRow>
                    <TableCell className="doctype-row" colSpan={9}>
                      <Typography sx={{ fontWeight: "700" }}>
                        {group.slpNumber}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {group.goalsAndAssessment.map((goal) => (
                    <TableRow key={goal.id}>
                      <TableCell className="break-word">
                        {studentName}
                      </TableCell>
                      <TableCell className="break-word">
                        {goal.studentGoals}
                      </TableCell>
                      <TableCell className="break-word">
                        {goal.smartGoals}
                      </TableCell>
                      <TableCell align="center">{goal.importance}</TableCell>
                      <TableCell align="center">{goal.difficulty}</TableCell>
                      <TableCell align="center">{goal.goalWeight}</TableCell>
                      <TableCell className="break-word">
                        {goal.baselineLevel}
                      </TableCell>
                      <TableCell>
                        {ratingDisplayMap[goal.achievementRatings] || NO_DATA}
                      </TableCell>
                      <TableCell className="break-word">
                        {goal.variance || NO_DATA}
                      </TableCell>
                      <TableCell>
                        {goal.goalAssessedDate
                          ? moment(goal.goalAssessedDate).format(
                              DATE_VIEW_FORMAT
                            )
                          : NO_DATA}
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleDeleteModal(goal.id)}>
                          <img src={deleteIcon} alt="Delete" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {slpGroupedGoalsListMeta && !!slpGroupedGoalsList.length ? (
          <CustomPagination
            handleChange={handleChange}
            page={page}
            pageNo={pageNo}
            onPageChange={onPageChange}
            listMeta={slpGroupedGoalsListMeta}
          />
        ) : (
          <NoRecords />
        )}
      </Card>
      {openGoalsModal && (
        <AddSLPGoalsModal
          nextOrCurrentSLPId={nextOrCurrentSLPId}
          openGoalsModal={openGoalsModal}
          setOpenGoalsModal={setOpenGoalsModal}
          updateGoalsListCallback={updateGoalsListCallback}
          addPersonalAndSmartGoalsRequest={addPersonalAndSmartGoalsRequest}
        />
      )}
      <DeleteConfirmationModal
        isOpenDeleteConfirmationModal={isOpenDocumentDeleteModal}
        handleDeleteConfirmationModalClose={handleDocumentDeleteCloseModal}
        deleteConfirmationMessage={DELETE_PERSONAL_SMART_GOALS_CONFIRMATION_MSG}
        handleYesClick={handleDeleteConfirm}
      />
    </Grid>
  );
};

export default SLPGoalsAndAssessment;
